import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  IconButton,
  Collapse, useColorModeValue, Tooltip,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import CampaignTable from "./CampaignTable";
import { ViewIcon } from "@chakra-ui/icons";
import theme from "../../theme";
import { RiSparkling2Fill } from "react-icons/ri";

const CampaignTableRow = ({
                            title,
                            campaigns,
                            handlePreviewClick,
                            changeArchiveStatus,
                            loadSettingsEditor,
                            loadTemplateEditor
                          }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonBg = useColorModeValue("gray.50", "gray.600");
  const iconColor = useColorModeValue(theme.colors.lightText, theme.colors.darkText);
  const buttonHoverBg = useColorModeValue("gray.100", "gray.600");

  const handleToggle = () => setIsOpen(!isOpen);

  function formatHourToAmPm(hour) {
    // Ensure hour is within valid range
    if (hour < 0 || hour >= 24) {
      throw new Error("Hour must be between 0 and 23.99.");
    }

    // Separate the whole number part (hours) and the decimal part (minutes)
    const wholeHour = Math.floor(hour);
    const minutes = Math.round((hour - wholeHour) * 60);

    // Determine if it's AM or PM
    const period = wholeHour >= 12 ? 'pm' : 'am';

    // Convert 24-hour format to 12-hour format
    const formattedHour = wholeHour % 12 || 12; // `0` or `12` should be `12`

    // Format minutes to always be two digits
    const formattedMinutes = minutes.toString().padStart(2, '0');

    // Return formatted string
    return `${formattedHour}:${formattedMinutes}${period}`;
  }

  const getStatus = (campaign) => {
    if (campaign.archived) {
      return "archived";
    }
    if (
      (campaign.status === "sent" || campaign.status === "error") &&
      campaign?.settings?.sending_date?.fixed_date
    ) {
      return (
        campaign.status +
        " on " +
        moment(campaign.settings.sending_date.fixed_date).format(
          "MMM D, YYYY hh:mm a"
        )
      );
    }

    if (campaign.purpose === "campaign-welcome") {
      const sendAfterHours = campaign.settings.sending_date.same_day?.send_after_time || 0;
      const sendAfterTime = sendAfterHours ?
        `after ${formatHourToAmPm(sendAfterHours)} ` : ` `;
      return `sending same day ${sendAfterTime}(${campaign.active ? "active" : "inactive"})`;
    }

    if (campaign.purpose === 'campaign-review') {
      const hours = campaign.settings?.sending_date?.hours_since_the_event;
      if (hours) {
        const suffix = hours === 1 ? "" : "s";
        return `sending ${hours} hour${suffix} after appointment (${campaign.active ? "active" : "inactive"})`;
      }
      return (campaign.active ? "active" : "inactive");
    }

    if (campaign.purpose === 'campaign-confirmation') {
      const hours = campaign.settings?.sending_date?.hours_since_the_event;
      if (hours) {
        const suffix = hours === 1 ? "" : "s";
        return `sending ${hours} hour${suffix} after booking (${campaign.active ? "active" : "inactive"})`;
      }
      return (campaign.active ? "active" : "inactive");
    }

    if (campaign.settings?.sending_date?.same_day?.enabled) { // same day
      const hours = campaign.settings.sending_date.same_day?.hours_before || 0;
      const sendAfterHours = campaign.settings.sending_date.same_day?.send_after_time || 0;
      const suffix = hours === 1 ? "" : "s";
      const sendingTime = hours ?
        `, ${hours} hour${suffix} before appointment,` : ``;
      const sendAfterTime = sendAfterHours ?
        `after ${formatHourToAmPm(sendAfterHours)}` : ``;
      return `sending same day${sendingTime} ${sendAfterTime} (${campaign.active ? "active" : "inactive"})`;
    }

    if (campaign.settings?.sending_date?.previous_day?.days_before) {
      const day = campaign.settings.sending_date.previous_day.days_before;
      return `sending ${day} day${day === 1 ? "" : "s"} before at ${
        formatHourToAmPm(campaign.settings.sending_date.previous_day.send_time)
      } (${campaign.active ? "active" : "inactive"})`;
    }

    if (campaign.purpose === 'campaign-manual') {
      return campaign.status;
    }

    return (campaign.active ? "active" : "inactive");
  };

  const columns = [
    {
      header: "Created",
      accessor: "created",
      format: (value) => moment(value).format("MMM D, YYYY"),
      className: "created-column"
    },
    {
      header: "Name",
      accessor: "name",
      format: (value, row) => (
        <Flex align="center" gap={2}>
          {row.author_entity === "po" && (
            <Tooltip label={"Created by AI"}>
              <span>
                <Icon as={RiSparkling2Fill} boxSize={4} color={iconColor} />
              </span>
            </Tooltip>
          )}
          <Text>{value}</Text>
        </Flex>
      ),
    }, // Left aligned by default
    { header: "Medium", accessor: "type" }, // Left aligned by default
    { header: "Status", accessor: "status", format: (_, row) => getStatus(row) }, // Left aligned by default
    {
      header: "Preview",
      accessor: "preview",
      format: (_, row) => (
        <IconButton
          icon={<Icon as={ViewIcon} color={iconColor}/>}
          bg={buttonBg}
          _hover={{ backgroundColor: buttonHoverBg }}
          borderRadius={8}
          aria-label="Preview"
          isDisabled={!row.template}
          onClick={() => handlePreviewClick(row)}
          size="sm"
        />
      ),
    },
  ];

  const actions = [
    {
      label: "Edit settings",
      onClick: (row) => loadSettingsEditor(row),
      show: (row) => !row.archived,
    },
    {
      label: "Edit template",
      onClick: (row) => loadTemplateEditor(row),
      show: (row) => !row.archived,
    },
    {
      label: (row) => (row.archived ? "Unarchive" : "Archive"),
      onClick: (row) => changeArchiveStatus(row._id, !row.archived),
    },
  ];

  if (campaigns.length === 0) {
    return null; // Don't render the section if there are no campaigns
  }
  campaigns = campaigns.sort((a, b) => new Date(b.created) - new Date(a.created));

  return (
    <Box display="flex" flexDirection="column" mb={4}>
      {title && (
        <Flex align="center" cursor="pointer" onClick={handleToggle}>
          <Text fontSize="lg" fontWeight="bold">
            {title}
          </Text>
          <IconButton
            icon={<FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />}
            aria-label="Expand/Collapse"
            size="sm"
            variant="ghost"
            ml={2}
          />
        </Flex>
      )}
      <Collapse in={isOpen || !title} animateOpacity>
        <CampaignTable data={campaigns} columns={columns} actions={actions} />
      </Collapse>
    </Box>
  );
};

export default CampaignTableRow;
